import { StanButton } from '@/components';
import { DropdownProvider } from '@/components/core-components/StanDropdown/DropdownProvider';
import { StanDropdownMenu } from '@/components/core-components/StanDropdown/Menu/StanDropdownMenu';
import { cn, createIconElement } from '@/lib';
import { useModal } from '@/hooks/useModal';
import { Overview } from './Overview';
import { AttendeesInfo } from './AttendeesInfo';
import { RescheduleModal } from './RescheduleModal';
import { CancelAppointmentModal } from './CancelAppointmentModal';
import { RescheduleSuccessModal } from './RescheduleSuccessModal';
import type { Booking } from '../../types/appointment.types';
import { BOOKING_TYPES } from '@/lib/constants';
import ExportIconSvg from '@/assets/icons/ic-download.svg';
import IcMoreVertical from '@/assets/icons/ic-more-vertical.svg';

const ExportIcon = createIconElement(ExportIconSvg);
const MoreVerticalIcon = createIconElement(IcMoreVertical, { className: 'size-5' });

interface AppointmentDetailsContentProps {
    booking: Booking;
    onExportDetails: (bookingId: string) => void;
    onReschedule?: (data: { date: string; startTime: string; endTime: string; reason?: string }) => Promise<void>;
    onCancel?: () => Promise<void>;
    isRescheduling?: boolean;
    isCanceling?: boolean;
}

export function AppointmentDetailsContent({
    booking,
    onExportDetails,
    onReschedule,
    onCancel,
    isRescheduling = false,
    isCanceling = false,
}: AppointmentDetailsContentProps) {
    const rescheduleModal = useModal();
    const cancelModal = useModal();
    const successModal = useModal();

    // Match Vue logic exactly
    const isDataReady = booking && 'booking_status' in booking;
    const appointmentStartTime = new Date(booking.start_utc).getTime();
    const now = new Date().getTime();

    // isUpcoming: appointment is in the future (any time)
    const isUpcoming = appointmentStartTime > now;

    // isInFuture: at least 1 minute in the future (for reschedule)
    const isInFuture = appointmentStartTime - 60000 > now;

    // Button availability logic matching Vue
    const canShowRescheduleOption =
        isDataReady && isInFuture && booking.booking_status !== 0 && booking.booking_type !== BOOKING_TYPES.WEBINAR;

    const cancelOptionAvailable = isDataReady && isUpcoming && booking.booking_status !== 0;

    const exportOptionAvailable = isDataReady && booking.booking_status !== 0 && (booking.attendees?.length || 0) > 0;

    // Dropdown options logic matching Vue
    const dropDownOptions: Array<{ label: string; value: string }> = [];
    if (canShowRescheduleOption || cancelOptionAvailable) {
        dropDownOptions.push({ label: 'Export Details', value: 'export' });
    }
    if (canShowRescheduleOption && cancelOptionAvailable) {
        dropDownOptions.push({ label: 'Cancel', value: 'cancel' });
    }
    const showDropdownOptions = dropDownOptions.length > 0;

    // Get first attendee name for display
    const firstAttendeeName = booking.attendees?.[0]?.name || 'attendee';

    const handleRescheduleSave = async (data: {
        date: string;
        startTime: string;
        endTime: string;
        reason?: string;
    }) => {
        if (onReschedule) {
            await onReschedule(data);
            rescheduleModal.close();
            successModal.open();
        }
    };

    const handleCancelConfirm = async () => {
        if (onCancel) {
            await onCancel();
            cancelModal.close();
        }
    };

    return (
        <div className='flex flex-col gap-6'>
            <div className='mb-2 flex items-center justify-between'>
                <h2 className='text-dark heading-heading2 m-0'>Overview</h2>

                <div className='flex items-center gap-2'>
                    {canShowRescheduleOption ? (
                        <StanButton
                            name='appointment-details-reschedule-booking-button'
                            onClick={rescheduleModal.open}
                            variant='outline'
                            size='sm'
                            type='button'
                            className={cn(!showDropdownOptions && 'mr-4')}
                        >
                            Reschedule
                        </StanButton>
                    ) : cancelOptionAvailable ? (
                        <StanButton
                            name='appointment-details-cancel-booking-button'
                            onClick={cancelModal.open}
                            variant='outline'
                            size='sm'
                            type='button'
                            className={cn(!showDropdownOptions && 'mr-4')}
                        >
                            Cancel
                        </StanButton>
                    ) : exportOptionAvailable ? (
                        <StanButton
                            name='appointment-details-export-booking-button'
                            onClick={() => onExportDetails(booking.booking_id)}
                            icon={ExportIcon}
                            variant='outline'
                            size='sm'
                            type='button'
                            className='mr-4'
                        >
                            Export Details
                        </StanButton>
                    ) : null}
                    {showDropdownOptions && (
                        <DropdownProvider dropdownId='appointment-actions' dropdownName='appointment-actions'>
                            <StanDropdownMenu
                                id='appointment-actions'
                                name='appointment-actions'
                                options={dropDownOptions}
                                onSelect={value => {
                                    if (value.value === 'export') {
                                        onExportDetails(booking.booking_id);
                                    } else if (value.value === 'cancel') {
                                        cancelModal.open();
                                    }
                                }}
                                placement='bottom-end'
                            >
                                <button
                                    type='button'
                                    className='border-neutral-subtle hover:bg-neutral-subtle flex size-9 cursor-pointer items-center justify-center rounded-lg transition-colors'
                                    aria-label='More actions'
                                >
                                    {MoreVerticalIcon}
                                </button>
                            </StanDropdownMenu>
                        </DropdownProvider>
                    )}
                </div>
            </div>

            <div className='shadow-nucleus-elevation-medium bg-canvas flex flex-col gap-8 rounded-lg p-6'>
                <Overview booking={booking} />
                <AttendeesInfo booking={booking} />
            </div>

            {/* Modals */}
            <RescheduleModal
                isOpen={rescheduleModal.isOpen}
                onClose={rescheduleModal.close}
                bookingTitle={booking.title}
                attendeeName={firstAttendeeName}
                bookingId={booking.booking_id}
                currentStartDateTime={booking.start}
                onSave={handleRescheduleSave}
                isLoading={isRescheduling}
            />

            <CancelAppointmentModal
                isOpen={cancelModal.isOpen}
                onClose={cancelModal.close}
                bookingTitle={booking.title}
                attendeeName={firstAttendeeName}
                onDelete={handleCancelConfirm}
                isDeleting={isCanceling}
            />

            <RescheduleSuccessModal
                isOpen={successModal.isOpen}
                onClose={successModal.close}
                attendeeName={firstAttendeeName}
            />
        </div>
    );
}
